import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import type { Cohort, Member } from '@fatigue/shared';
import {
  assignClinician,
  createCohort,
  listClinicians,
  listCohorts,
  unassignClinician,
} from '../../data/api';

/**
 * Cohorts, and who runs them.
 *
 * This screen is where clinical access is actually granted, so it shows the
 * consequence rather than leaving it implied: assigning somebody here is what
 * lets them read those patients' records, and the copy says that in as many
 * words.
 *
 * Patients are shown as a **count**, never a roster. The owner needs to know a
 * cohort is not empty in order to run the organisation; the names belong to the
 * clinicians who treat them. The API does not return them either — this is not
 * a display choice that a future change could quietly undo.
 */
export function Cohorts(): JSX.Element {
  const [cohorts, setCohorts] = useState<readonly Cohort[]>([]);
  const [members, setMembers] = useState<readonly Member[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  // In flight, so a second click cannot send the same request again. State
  // for the disabled attribute; nothing else is fast enough to stop a double
  // click, because the first click's re-render may not have landed yet.
  const [creating, setCreating] = useState(false);
  /** `${cohortId}/${userId}` for each assignment change still on its way. */
  const [changing, setChanging] = useState<ReadonlySet<string>>(new Set());

  const reload = (): Promise<void> =>
    Promise.all([listCohorts(), listClinicians()])
      .then(([loadedCohorts, loadedMembers]) => {
        setCohorts(loadedCohorts);
        setMembers(loadedMembers.filter((member) => member.status === 'active'));
        setError(null);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Could not load cohorts.');
      })
      .finally(() => setLoading(false));

  useEffect(() => {
    void reload();
  }, []);

  const report = (err: unknown, fallback: string): void => {
    setError(err instanceof Error ? err.message : fallback);
  };

  const create = (event: React.FormEvent): void => {
    event.preventDefault();
    const name = newName.trim();
    if (name === '' || creating) return;

    setCreating(true);
    createCohort(name)
      .then(() => {
        setNewName('');
        return reload();
      })
      .catch((err: unknown) => report(err, 'Could not create that cohort.'))
      .finally(() => setCreating(false));
  };

  const toggle = (cohort: Cohort, member: Member, assigned: boolean): void => {
    const key = `${cohort.cohortId}/${member.userId}`;
    if (changing.has(key)) return;
    setChanging((current) => new Set(current).add(key));

    const action = assigned
      ? unassignClinician(cohort.cohortId, member.userId)
      : assignClinician(cohort.cohortId, member.userId);

    // Held until the reload lands, not just the request: until then the box
    // still shows the old state, and a click would ask for the same change.
    action
      .then(reload)
      .catch((err: unknown) => report(err, 'Could not change that assignment.'))
      .finally(() =>
        setChanging((current) => {
          const next = new Set(current);
          next.delete(key);
          return next;
        }),
      );
  };

  if (loading) return <p className="admin__note">Loading…</p>;

  return (
    <section className="admin__section">
      <header className="admin__head">
        <div>
          <h2 className="admin__title">Cohorts</h2>
          <p className="admin__sub">
            A cohort is a course and the clinicians who run it. Assigning a clinician here is what
            lets them read those patients&rsquo; records.
          </p>
        </div>
      </header>

      {error !== null && (
        <p className="admin__error" role="alert">
          {error}
        </p>
      )}

      <form className="admin__inline" onSubmit={create}>
        <label className="visually-hidden" htmlFor="new-cohort">
          Name of the new cohort
        </label>
        <input
          id="new-cohort"
          className="invite__input"
          value={newName}
          placeholder="Autumn intake"
          onChange={(event) => setNewName(event.target.value)}
          disabled={creating}
        />
        <button type="submit" className="primary" disabled={creating || newName.trim() === ''}>
          {creating ? 'Creating…' : 'Create cohort'}
        </button>
      </form>

      {cohorts.length === 0 && (
        <p className="admin__note">
          No cohorts yet. Create one, assign a clinician to it, and they can start inviting
          patients.
        </p>
      )}

      {cohorts.map((cohort) => {
        const assignedIds = new Set((cohort.clinicians ?? []).map((c) => c.userId));

        return (
          <article key={cohort.cohortId} className="cohort">
            <header className="cohort__head">
              <h3 className="cohort__name">{cohort.name}</h3>
              <span className="cohort__count">
                {cohort.patientCount === 1 ? '1 patient' : `${cohort.patientCount ?? 0} patients`}
              </span>
            </header>

            <p className="cohort__label">Clinicians running this cohort</p>
            <ul className="cohort__people">
              {members.map((member) => {
                const assigned = assignedIds.has(member.userId);
                return (
                  <li key={member.userId}>
                    <label className="cohort__person">
                      <input
                        type="checkbox"
                        checked={assigned}
                        disabled={changing.has(`${cohort.cohortId}/${member.userId}`)}
                        onChange={() => toggle(cohort, member, assigned)}
                      />
                      <span>
                        {member.displayName}
                        {member.role === 'owner' && (
                          <span className="cohort__owner"> &middot; owner</span>
                        )}
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>

            {assignedIds.size === 0 && (
              <p className="cohort__warning">
                Nobody runs this cohort, so nobody can invite patients into it or read the records
                of anybody already in it.
              </p>
            )}
          </article>
        );
      })}
    </section>
  );
}
